/* eslint-disable max-lines */
import { useTranslation } from 'react-i18next';
import logo from '@assets/images/scriptix-blue-logo@2-1.png';
import logoDark from '@assets/images/scriptix-white-400x99.png';
import { useGetHeartbeat } from '@utils/api/use-get-scriptix-status.tsx';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PATHS } from '../../router/paths.ts';
import { toast } from 'react-hot-toast';
import { Check, WifiOff, X } from 'lucide-react';
import { useThemeContext } from '@utils/providers/theme-context.tsx';
import { fmtPercent } from '@utils/format';
import { Heading, Text } from '@components/atoms/ui-kit/text';
import { Card } from '@components/atoms/ui-kit/card';
import { Skeleton } from '@components/atoms/ui-kit/skeleton';
import { EmptyState } from '@components/atoms/ui-kit/empty-state';

// Status indicator colors - semantic colors that remain consistent across themes
/* eslint-disable no-restricted-syntax */
const STATUS_COLORS = {
  up: '#22c55e', // green-500
  degraded: '#eab308', // yellow-500
  down: '#ef4444', // red-500
  unknown: '#9ca3af' // gray-400
} as const;
/* eslint-enable no-restricted-syntax */

const StatusPage = () => {
  const { t } = useTranslation();
  const { data, isLoading, error } = useGetHeartbeat('scriptix');
  const navigate = useNavigate();
  const { getColour, theme } = useThemeContext();

  // Monitor ID to service name mapping
  const MONITOR_NAMES: Record<string, string> = {
    '19': 'API',
    '21': 'UI'
  };

  // Helper function to determine service status based on recent heartbeats
  const getServiceStatus = (
    heartbeats: Array<{
      status: number;
      time: string;
      msg: string;
      ping: number;
    }>
  ): 'up' | 'degraded' | 'down' | 'unknown' => {
    if (!heartbeats || heartbeats.length === 0) return 'unknown';

    // Check last 10 heartbeats (or all if less than 10)
    const recentHeartbeats = heartbeats.slice(-10);
    const failedCount = recentHeartbeats.filter(h => h.status !== 1).length;

    // Current status is based on the most recent heartbeat
    const currentStatus = heartbeats[heartbeats.length - 1].status;

    if (currentStatus !== 1) return 'down';
    if (failedCount > 0) return 'degraded';
    return 'up';
  };

  const services = data
    ? Object.entries(data.heartbeatList).map(([monitorId, heartbeat]) => {
        const serviceName = MONITOR_NAMES[monitorId] || `Monitor ${monitorId}`;
        const serviceStatus = getServiceStatus(heartbeat);
        const uptime = data.uptimeList[`${monitorId}_24`] * 100;

        return {
          monitorId,
          name: serviceName,
          uptime: fmtPercent(uptime),
          status: serviceStatus
        };
      })
    : [];

  let lastUpdated = '';
  if (data) {
    const all = Object.values(data.heartbeatList).flat();
    const last = all[all.length - 1];
    lastUpdated = last?.time || '';
  }

  // Detect real incidents (status changes from up to down or down to up)
  const incidents = data
    ? Object.entries(data.heartbeatList)
        .map(([monitorId, heartbeat]) => {
          const serviceName =
            MONITOR_NAMES[monitorId] || `Monitor ${monitorId}`;
          const statusChanges: Array<{
            monitorId: string;
            serviceName: string;
            date: string;
            title: string;
            type: 'outage' | 'recovery';
            resolved: string | null;
          }> = [];

          // Detect status transitions
          for (let i = 1; i < heartbeat.length; i++) {
            const prev = heartbeat[i - 1];
            const curr = heartbeat[i];

            // Outage detected: previous was up, current is down
            if (prev.status === 1 && curr.status !== 1) {
              statusChanges.push({
                monitorId,
                serviceName,
                date: curr.time,
                title: `${serviceName} Outage Detected`,
                type: 'outage',
                resolved: null
              });
            }
            // Recovery detected: previous was down, current is up
            else if (prev.status !== 1 && curr.status === 1) {
              statusChanges.push({
                monitorId,
                serviceName,
                date: curr.time,
                title: `${serviceName} Recovered`,
                type: 'recovery',
                resolved: curr.time
              });
            }
          }

          return statusChanges;
        })
        .flat()
        .sort((a, b) => {
          const dateA = new Date(a.date).getTime();
          const dateB = new Date(b.date).getTime();
          // Handle invalid dates by treating them as oldest
          if (isNaN(dateB) && isNaN(dateA)) return 0;
          if (isNaN(dateB)) return -1;
          if (isNaN(dateA)) return 1;
          return dateB - dateA;
        })
        .slice(0, 20) // Show only last 20 incidents
    : [];

  // Calculate overall system status
  const getOverallStatus = ():
    | {
        status: 'operational' | 'degraded' | 'outage' | 'unknown';
        message: string;
      }
    | undefined => {
    if (!services || services.length === 0) {
      return { status: 'unknown', message: t('loading-system-status') };
    }

    const downServices = services.filter(s => s.status === 'down');
    const degradedServices = services.filter(s => s.status === 'degraded');
    const unknownServices = services.filter(s => s.status === 'unknown');

    // Check for any down services
    if (downServices.length > 0) {
      return {
        status: 'outage',
        message: t('some-systems-experiencing-issues')
      };
    }

    // Check for degraded services
    if (degradedServices.length > 0) {
      return {
        status: 'degraded',
        message: t('all-systems-operational-minor-issues')
      };
    }

    // Check for unknown status services
    if (unknownServices.length > 0) {
      return {
        status: 'unknown',
        message: t('unable-to-determine-system-status')
      };
    }

    // Check if uptime is less than 100% for any service
    const hasLowUptime = services.some(s => {
      const uptimeValue = parseFloat(s.uptime.replace('%', ''));
      return uptimeValue < 100;
    });

    if (hasLowUptime) {
      return {
        status: 'degraded',
        message: t('all-systems-operational-minor-issues')
      };
    }

    return { status: 'operational', message: t('all-systems-operational') };
  };

  const overallStatus = getOverallStatus();

  const overallUptime = data
    ? ((data.uptimeList['19_24'] + data.uptimeList['21_24']) / 2) * 100
    : 0;

  const [countdown, setCountdown] = useState(60);

  useEffect(() => {
    const interval = setInterval(() => {
      setCountdown(prev => (prev === 0 ? 60 : prev - 1));
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const handleEmailClick = () => {
    if (error) {
      toast.error(t('there-was-an-error-fetching-the-data-please-try-again'));
    } else {
      const subject = encodeURIComponent('Service Status Report');
      const serviceStatus = services
        .map(s => `- ${s.name}: ${s.status.toUpperCase()} (${s.uptime} uptime)`)
        .join('\n');
      const incidentList =
        incidents.length > 0
          ? incidents
              .map(i => `- [${i.type.toUpperCase()}] ${i.title} at ${i.date}`)
              .join('\n')
          : 'No incidents detected';

      const body = encodeURIComponent(
        `Please find the service status report below:

Overall Status: ${overallStatus?.status.toUpperCase()}
Overall Uptime: ${fmtPercent(overallUptime)}

Services:
${serviceStatus}

Recent Incidents:
${incidentList}`
      );

      window.location.href = `mailto:support@scriptix.com?subject=${subject}&body=${body}`;
    }
  };

  const apiUptime = data ? fmtPercent(data.uptimeList['19_24'] * 100) : '0%';
  const uiUptime = data ? fmtPercent(data.uptimeList['21_24'] * 100) : '0%';

  const pageContainer = (children: React.ReactNode) => (
    <main
      className="py-4 min-h-screen"
      style={{ backgroundColor: getColour('baseLayer') }}
    >
      <h1 className="sr-only">{t('status')}</h1>
      <div className="w-full h-screen flex items-center justify-center sm:p-8 p-4">
        <div
          className="w-full max-w-5xl rounded-2xl shadow-md p-12"
          style={{ backgroundColor: getColour('secondLayer') }}
        >
          <img
            src={theme === 'dark' ? logoDark : logo}
            alt="Scriptix Logo"
            className="mx-auto w-44 cursor-pointer mb-6"
            onClick={() => navigate(PATHS().LOGIN)}
          />
          {children}
        </div>
      </div>
    </main>
  );

  if (isLoading) {
    return pageContainer(
      <Skeleton variant="card" className="max-w-sm mx-auto" />
    );
  }

  if (error) {
    return pageContainer(
      <EmptyState
        icon={<WifiOff className="h-7 w-7" />}
        title={t('unable-to-load-system-status')}
        subtitle={t('please-try-again-or-contact-support')}
      />
    );
  }

  const getHeaderGradient = () => {
    const baseColor = getColour('firstLayer');
    if (overallStatus?.status === 'outage') {
      return theme === 'dark'
        ? 'linear-gradient(to bottom, rgba(127, 29, 29, 0.3), rgba(127, 29, 29, 0.1))'
        : 'linear-gradient(to bottom, #fee2e2, #fef2f2)';
    }
    if (overallStatus?.status === 'degraded') {
      return theme === 'dark'
        ? 'linear-gradient(to bottom, rgba(113, 63, 18, 0.3), rgba(113, 63, 18, 0.1))'
        : 'linear-gradient(to bottom, #fef3c7, #fffbeb)';
    }
    return theme === 'dark'
      ? `linear-gradient(to bottom, rgba(30, 58, 138, 0.2), ${baseColor})`
      : 'linear-gradient(to bottom, #dbeafe, #eff6ff)';
  };

  return (
    <main
      className="py-4 min-h-screen"
      style={{ backgroundColor: getColour('baseLayer') }}
    >
      <div className="w-full h-screen flex items-center justify-center sm:p-8 p-4">
        <div
          className="w-full max-w-5xl rounded-2xl shadow-md overflow-y-auto max-h-screen"
          style={{ backgroundColor: getColour('secondLayer') }}
        >
          {/* Header */}
          <header
            className="py-8 text-center"
            style={{ background: getHeaderGradient() }}
          >
            <img
              src={theme === 'dark' ? logoDark : logo}
              alt="Scriptix Logo"
              className="mx-auto w-44 cursor-pointer"
              onClick={() => navigate(PATHS().LOGIN)}
            />
            <Heading level={1} size="2xl" weight="semibold" className="mt-4">
              {overallStatus?.message || t('loading-system-status')}
            </Heading>
            {overallStatus?.status === 'degraded' && (
              <Text as="p" size="sm" tone="muted" className="mt-2">
                {t('some-services-intermittent-issues')}
              </Text>
            )}
            {overallStatus?.status === 'outage' && (
              <Text as="p" size="sm" tone="muted" className="mt-2">
                {t('actively-resolving')}
              </Text>
            )}
          </header>

          {/* 2x2 Panel Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6">
            {/* Panel 1: Minimal List View */}
            <Card
              padding="sm"
              style={{ backgroundColor: getColour('firstLayer') }}
            >
              <Heading level={2} size="lg" className="mb-4">
                {t('services-overview')}
              </Heading>
              <ul className="space-y-3">
                {services.map(svc => (
                  <li key={svc.name} className="flex items-center">
                    <span
                      className="inline-block w-3 h-3 rounded-full mr-3"
                      style={{ backgroundColor: STATUS_COLORS[svc.status] }}
                    />
                    <Text as="span" weight="medium">
                      {svc.name}
                    </Text>
                    <Text as="span" size="sm" tone="muted" className="ml-auto">
                      {svc.uptime}
                    </Text>
                  </li>
                ))}
              </ul>
            </Card>

            {/* Panel 2: Card Grid */}
            <Card
              padding="sm"
              style={{ backgroundColor: getColour('firstLayer') }}
            >
              <Heading level={2} size="lg" className="mb-4">
                {t('services-cards')}
              </Heading>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {services.map(svc => (
                  <div
                    key={svc.name}
                    className="flex flex-col items-center text-center p-4 rounded-2xl border"
                    style={{
                      backgroundColor: getColour('thirdLayer'),
                      borderColor: getColour('border')
                    }}
                  >
                    {svc.status === 'up' ? (
                      <Check
                        className="h-6 w-6"
                        style={{ color: STATUS_COLORS.up }}
                      />
                    ) : svc.status === 'degraded' ? (
                      <span
                        className="h-6 w-6 font-bold text-xl"
                        style={{ color: STATUS_COLORS.degraded }}
                      >
                        ⚠
                      </span>
                    ) : (
                      <X
                        className="h-6 w-6"
                        style={{ color: STATUS_COLORS.down }}
                      />
                    )}
                    <Text as="div" weight="semibold" className="mt-2">
                      {svc.name}
                    </Text>
                    <div
                      className="mt-1 text-sm"
                      style={{ color: STATUS_COLORS[svc.status] }}
                    >
                      {svc.status === 'up'
                        ? t('operational')
                        : svc.status === 'degraded'
                          ? t('degraded-performance')
                          : t('major-outage')}
                    </div>
                    <Text as="div" size="xs" tone="muted" className="mt-1">
                      {svc.uptime} {t('uptime')}
                    </Text>
                  </div>
                ))}
              </div>
            </Card>

            {/* Panel 3: Incident Timeline */}
            <Card
              padding="sm"
              style={{ backgroundColor: getColour('firstLayer') }}
            >
              <Heading level={2} size="lg" className="mb-4">
                {t('incident-timeline')}
              </Heading>
              <div
                className="border-l-2 pl-4 space-y-6 overflow-y-auto max-h-80"
                style={{ borderColor: getColour('border') }}
              >
                {incidents.length === 0 ? (
                  <EmptyState
                    title={t('no-incidents-detected')}
                    className="py-6"
                  />
                ) : (
                  incidents.map((inc, i) => (
                    <div key={i} className="relative">
                      <div
                        className="absolute -left-3 top-1 rounded-full w-3 h-3 border"
                        style={{
                          borderColor: getColour('border'),
                          backgroundColor:
                            inc.type === 'recovery'
                              ? STATUS_COLORS.up
                              : STATUS_COLORS.down
                        }}
                      />
                      <Text as="div" weight="medium" className="pl-1">
                        {inc.date}
                      </Text>
                      <Text
                        as="div"
                        size="sm"
                        weight="medium"
                        style={{
                          color:
                            inc.type === 'recovery'
                              ? STATUS_COLORS.up
                              : STATUS_COLORS.down
                        }}
                      >
                        {inc.title}
                      </Text>
                      {inc.type === 'recovery' && (
                        <Text as="div" size="xs" tone="muted">
                          {t('service-restored')}
                        </Text>
                      )}
                    </div>
                  ))
                )}
              </div>
            </Card>

            {/* Panel 4: Metrics & Subscribe */}
            <Card
              padding="sm"
              style={{ backgroundColor: getColour('firstLayer') }}
              className="flex flex-col items-center"
            >
              <Heading level={2} size="lg" className="w-full mb-4">
                {t('overview')}
              </Heading>
              <div className="mt-4 w-full space-y-3">
                <div className="flex justify-between items-center">
                  <Text as="span" size="sm" weight="medium" tone="muted">
                    {t('api-uptime-24h')}:
                  </Text>
                  <Text as="span" size="lg" weight="bold">
                    {apiUptime}
                  </Text>
                </div>
                <div className="flex justify-between items-center">
                  <Text as="span" size="sm" weight="medium" tone="muted">
                    {t('ui-uptime-24h')}:
                  </Text>
                  <Text as="span" size="lg" weight="bold">
                    {uiUptime}
                  </Text>
                </div>
                <div
                  className="flex justify-between items-center pt-2 border-t"
                  style={{ borderColor: getColour('border') }}
                >
                  <Text as="span" size="sm" weight="semibold">
                    {t('overall-uptime')}:
                  </Text>
                  <Text as="span" size="xl" weight="bold">
                    {fmtPercent(overallUptime, 2)}
                  </Text>
                </div>
              </div>
              <div className="mt-6 w-full">
                <Text
                  as="div"
                  size="xs"
                  tone="muted"
                  className="mb-2 text-center"
                >
                  {t('response-time-chart')}
                </Text>
                <div className="flex items-end space-x-1 w-full overflow-x-auto">
                  {data &&
                    data.heartbeatList['19']?.map((heartbeat, idx) => {
                      /* eslint-disable no-restricted-syntax */
                      const chartBarColor =
                        heartbeat.status !== 1
                          ? STATUS_COLORS.down
                          : heartbeat.ping > 200
                            ? STATUS_COLORS.degraded
                            : theme === 'dark'
                              ? '#6b7280'
                              : '#94a3b8';
                      /* eslint-enable no-restricted-syntax */
                      return (
                        <div
                          key={idx}
                          className="flex-1 rounded-sm min-w-[2px]"
                          style={{
                            height: `${Math.min(Math.max(heartbeat.ping / 5, 2), 60)}px`,
                            backgroundColor: chartBarColor
                          }}
                          title={`${heartbeat.time}: ${heartbeat.ping}ms`}
                        />
                      );
                    })}
                </div>
              </div>
              <div className="mt-6 flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-6">
                <div>
                  <Text as="div" size="2xl" weight="semibold">
                    {incidents.length}
                  </Text>
                  <Text as="div" size="sm" tone="muted">
                    {t('incidents-30d')}
                  </Text>
                </div>
                <div className="flex space-x-3">
                  <button
                    className="px-4 py-2 rounded-2xl text-white"
                    style={{ backgroundColor: getColour('activeText') }}
                    onClick={handleEmailClick}
                  >
                    {t('email')}
                  </button>
                </div>
              </div>
            </Card>
          </div>

          {/* Footer with details */}
          <footer
            className="border-t mt-6 py-4 text-center space-y-1"
            style={{ borderColor: getColour('border') }}
          >
            <Text as="div" size="sm" weight="medium">
              {t('powered-by-scriptix')}
            </Text>
            <Text as="div" size="sm" tone="muted">
              {t('last-updated')}:{' '}
              {isLoading ? t('loading') : error ? t('error') : lastUpdated}
            </Text>
            <Text as="div" size="sm" tone="muted">
              {t('refresh-in')}: {countdown}s
            </Text>
          </footer>
        </div>
      </div>
    </main>
  );
};

export default StatusPage;
